/*
  Collapses a long variant option list (ring sizes, chain lengths) behind a
  trigger. Presentation only — the radios stay in the DOM inside their
  fieldset, so VariantSelects and product-info keep working untouched. This
  element only shows and hides the panel around them.

  Below 750px the panel is a modal bottom sheet over the shared .ds-sheet-scrim
  (scroll lock, focus trap, Escape); above it, a non-modal inline popover
  inside the buy column.
*/
if (!customElements.get('variant-sheet')) {
  const SHEET_QUERY = '(max-width: 749px)';
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])';

  customElements.define(
    'variant-sheet',
    class VariantSheet extends HTMLElement {
      connectedCallback() {
        if (this.dataset.bound === 'true') return;
        this.dataset.bound = 'true';

        this.trigger = this.querySelector('[data-sheet-trigger]');
        this.panel = this.querySelector('[data-sheet-panel]');
        this.scrim = this.querySelector('[data-sheet-scrim]');

        if (!this.trigger || !this.panel) return;

        this.mediaQuery = window.matchMedia(SHEET_QUERY);
        this.lockOwner = `variant-sheet:${this.panel.id || 'sheet'}`;
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);

        this.trigger.setAttribute('aria-haspopup', 'dialog');
        this.trigger.addEventListener('click', () => this.toggle());
        this.querySelector('[data-sheet-close]')?.addEventListener('click', () => this.close({ focus: true }));
        this.scrim?.addEventListener('click', () => this.close({ focus: true }));

        // Picking a size is the whole point of opening the panel, so close on
        // selection rather than making people dismiss it themselves.
        this.panel.addEventListener('change', (event) => {
          if (event.target.type !== 'radio') return;
          this.close({ focus: true });
        });
      }

      disconnectedCallback() {
        if (this.isOpen) this.close();
      }

      get isOpen() {
        return this.trigger?.getAttribute('aria-expanded') === 'true';
      }

      get isSheet() {
        return Boolean(this.mediaQuery?.matches);
      }

      toggle() {
        this.isOpen ? this.close({ focus: true }) : this.open();
      }

      open() {
        if (!this.trigger || this.isOpen) return;

        this.modal = this.isSheet;
        // A drawer that was already open (e.g. the cart quick-edit drawer this
        // sheet lives in) sits below us; any drawer opened later sits above.
        this.drawerBelow = window.DsDrawer?.top || null;

        this.trigger.setAttribute('aria-expanded', 'true');
        this.panel.hidden = false;
        this.classList.add('variant-sheet--open');

        if (this.modal) {
          this.panel.setAttribute('aria-modal', 'true');
          this.scrim?.classList.add('is-open');
          window.lockBodyScroll?.(this.lockOwner);
        } else {
          this.panel.removeAttribute('aria-modal');
        }

        document.addEventListener('click', this.onDocumentClick);
        window.addEventListener('keydown', this.onKeydown, true);

        const checked = this.panel.querySelector('input[type="radio"]:checked:not(:disabled)');
        const first = this.panel.querySelector('input[type="radio"]:not(:disabled)');
        (checked || first)?.focus({ preventScroll: true });
      }

      close({ focus = false } = {}) {
        if (!this.isOpen) return;

        this.trigger.setAttribute('aria-expanded', 'false');
        this.panel.hidden = true;
        this.panel.removeAttribute('aria-modal');
        this.scrim?.classList.remove('is-open');
        this.classList.remove('variant-sheet--open');

        if (this.modal) window.unlockBodyScroll?.(this.lockOwner);
        this.modal = false;
        this.drawerBelow = null;

        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('keydown', this.onKeydown, true);

        if (focus) this.trigger.focus({ preventScroll: true });
      }

      isTopOverlay() {
        const top = window.DsDrawer?.top || null;
        return !top || top === this.drawerBelow;
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) this.close();
      }

      // Window capture runs ahead of the ds-drawer Escape handler, so an open
      // sheet inside a drawer closes on its own instead of taking the drawer
      // with it.
      onKeydown(event) {
        if (!this.isTopOverlay()) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          this.close({ focus: true });
          return;
        }

        if (event.key === 'Tab' && this.modal) this.trapFocus(event);
      }

      trapFocus(event) {
        const seenGroups = new Set();
        const focusables = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter((el) => {
          if (el.offsetParent === null && el !== document.activeElement) return false;
          if (el.type !== 'radio') return true;
          // A radio group is one tab stop: its checked radio, else its first.
          const group = this.panel.querySelectorAll(`input[type="radio"][name="${CSS.escape(el.name)}"]`);
          const stop = Array.from(group).find((radio) => radio.checked && !radio.disabled) || group[0];
          if (el !== stop || seenGroups.has(el.name)) return false;
          seenGroups.add(el.name);
          return true;
        });
        if (!focusables.length) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        let target = null;

        if (!this.panel.contains(active)) target = event.shiftKey ? last : first;
        else if (event.shiftKey && active === first) target = last;
        else if (!event.shiftKey && active === last) target = first;

        if (!target) return;
        event.preventDefault();
        event.stopPropagation();
        target.focus();
      }
    }
  );
}
